import { PopularCategories } from './popular-categories';
import { ListingRequestCard } from 'app/components/listing-request-card';
import { MultipleOfferCard } from 'app/components/multiple-offer-card';
import { RequestEmptyState } from 'app/components/request-empty-state';
import type { Listing } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import React from 'react';
import { Strategies } from '../../all-listings/components/recommended-listings.utils';
import { DesktopRecommendationSection } from '../../all-listings/components/recommended-listings.large';
import { DesktopHeader } from '../desktop-header';
import type { useDesktopLandingData } from '../use-desktop-landing-data';
import { CountBadge } from './count-badge';
import { LandingCategories } from './landing-categories';
import { TrialBanner } from './trial-banner';
import { UpcomingActivity } from './upcoming-activity';
import { UnlockFullExperienceCard } from './unlock-full-experience-card';
import { RecentLoansDiscoverySection } from './recent-loans-discovery-section';
import { ProfileCompletionSteps, useHasPartagePlusSubscription } from '../shared';

interface LandingPageSection {
  key: string;
  node: React.ReactElement;
}

export function LandingOverviewSections({ data }: { data: ReturnType<typeof useDesktopLandingData> }) {
  const hasPartagePlusSubscription = useHasPartagePlusSubscription();
  const {
    profile,
    user,
    stats,
    unreadData,
    isLoggedIn,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    visibleExpiredLoans,
    navigation,
    actions,
    urgentNeedsRecommendations,
    urgentNeedsCount,
    recentOffersRecommendations,
    shouldShowRecommendationSection,
  } = data;
  const items: LandingPageSection[] = [
    {
      key: 'header',
      node: (
        <DesktopHeader
          activitySubtitle={activitySubtitle}
          addressLabel={addressLabel}
          greeting={greeting}
          isLoggedIn={isLoggedIn}
          navigation={navigation}
          profile={profile}
          statsLoaded={Boolean(stats && unreadData)}
          hasLateLoans={visibleExpiredLoans.length > 0}
          unreadCount={unreadCount}
          user={user}
        />
      ),
    },
    { key: 'prompts', node: <LandingOverviewPrompts data={data} /> },
    { key: 'upcoming-activity', node: <LandingOverviewActivity data={data} /> },
    ...(shouldShowRecommendationSection(urgentNeedsRecommendations, { hasEmptyState: true })
      ? [
          {
            key: 'urgent-needs',
            node: (
              <DesktopRecommendationSection<Listing>
                horizontalPadding={0}
                strategyProps={{ strategy: Strategies.UrgentNeeds }}
                recommendationState={urgentNeedsRecommendations}
                recommendations={urgentNeedsRecommendations.recommendations as Listing[] | undefined}
                onViewAll={actions.openRequests}
                renderItem={(listing) => <ListingRequestCard listing={listing} showUser />}
                emptyState={<RequestEmptyState onPressViewAll={actions.openRequests} />}
                titleSuffix={<CountBadge count={urgentNeedsCount} />}
              />
            ),
          },
        ]
      : []),
    ...(hasPartagePlusSubscription === false
      ? [
          {
            key: 'unlock-full-experience',
            node: <UnlockFullExperienceCard onPress={actions.openPayment} variant="large" />,
          },
        ]
      : []),
    ...(shouldShowRecommendationSection(recentOffersRecommendations)
      ? [
          {
            key: 'recent-offers',
            node: (
              <DesktopRecommendationSection<Listing>
                horizontalPadding={0}
                strategyProps={{ strategy: Strategies.RecentOffers }}
                recommendationState={recentOffersRecommendations}
                recommendations={recentOffersRecommendations.recommendations as Listing[] | undefined}
                onViewAll={actions.openRecentOffers}
              />
            ),
          },
        ]
      : []),
    ...(isWhiteLabel(WhiteLabel.Refrain) &&
    !data.isNeighbourhoodStatusPending &&
    (!data.isImmatureNeighbour || data.showDiscoveryFeeds)
      ? [{ key: 'recent-loans', node: <RecentLoansDiscoverySection data={data} /> }]
      : []),
    ...(data.showLandingCategories
      ? [
          {
            key: 'popular-categories',
            node: (
              <PopularCategories onCategoryPress={actions.openOfferCategory} onViewAll={actions.openExploreOffers} />
            ),
          },
        ]
      : []),
    ...(isWhiteLabel(WhiteLabel.HauteGaspesie)
      ? [
          {
            key: 'categories',
            node: (
              <LandingCategories
                onTagPress={actions.openOfferTag}
                onViewAll={actions.openExploreOffers}
                variant="large"
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {items.map((section) => (
        <React.Fragment key={section.key}>{section.node}</React.Fragment>
      ))}
    </>
  );
}

function LandingOverviewPrompts({ data }: { data: ReturnType<typeof useDesktopLandingData> }) {
  const {
    isLoggedIn,
    completeProfilePromptCompleted,
    hideStepsToComplete,
    completeProfileSteps,
    showTopMultipleOfferCard,
    dismissTopMultipleOfferCard,
    freeSubAvailable,
    freeSubNumDaysLeft,
    actions,
  } = data;
  return (
    <>
      {isLoggedIn && !completeProfilePromptCompleted && !hideStepsToComplete ? (
        <ProfileCompletionSteps {...completeProfileSteps} />
      ) : null}
      {showTopMultipleOfferCard ? <MultipleOfferCard onPublish={dismissTopMultipleOfferCard} /> : null}
      {freeSubAvailable && typeof freeSubNumDaysLeft === 'number' ? (
        <TrialBanner
          freeSubAvailable={freeSubAvailable}
          freeSubNumDaysLeft={freeSubNumDaysLeft}
          onSubscribe={actions.openMyPlan}
        />
      ) : null}
    </>
  );
}

function LandingOverviewActivity({ data }: { data: ReturnType<typeof useDesktopLandingData> }) {
  if (!data.isLoggedIn || !data.canViewActivitySummary) return null;
  return (
    <UpcomingActivity
      borrowings={data.borrowings ?? []}
      isLoading={!data.loansQuerySettled}
      lendings={data.lendings ?? []}
      onViewAll={data.actions.openManagementOverview}
      variant="large"
    />
  );
}
